
export const TIPO_DE_IDENTIFICACION_FISICA = "01";
export const TIPO_DE_IDENTIFICACION_JURIDICA = "02";
export const TIPO_DE_IDENTIFICACION_DIMEX = "03";
export const TIPO_DE_IDENTIFICACION_NITE = "04";
export const TIPO_DE_IDENTIFICACION_EXTRANJERO_NO_DOMICILIADO = "05";
export const TIPO_DE_IDENTIFICACION_NO_CONTRIBUYENTE = "06";

export const EASYPOS_DOCUMENTS_EMAIL = "documentos@easyposcr.com";

/**
 * Motor que genera los PDF de documentos y reportes.
 *
 * `"chromium"` usa la lambda `render-pdf` de este repo (Chromium headless, autenticada
 * con el idToken de Cognito). `"wkhtml"` vuelve al generador externo de 2016
 * (wkhtmltopdf sobre nodejs8.10, con la api-key en el bundle) y existe solo como
 * rollback: cambiar esta linea y desplegar el front revierte todo sin tocar el backend.
 *
 * Ver `src/utils/htmlExport.ts`.
 */
export const PDF_ENGINE: "chromium" | "wkhtml" = "chromium";

export const FREE_EMAIL_DOMAINS = [
    "gmail.com",
    "hotmail.com",
    "hotmail.es",
    "outlook.com",
    "outlook.es",
    "live.com",
    "live.com.mx",
    "msn.com",
    "yahoo.com",
    "yahoo.es",
    "ymail.com",
    "icloud.com",
    "me.com",
    "aol.com",
    "proton.me",
    "protonmail.com",
];

export const isFreeEmailDomain = (email?: string | null): boolean => {
    if (!email) return false;
    const domain = email.trim().toLowerCase().split("@")[1];
    if (!domain) return false;
    return FREE_EMAIL_DOMAINS.includes(domain);
};

export const TIPOS_DE_IDENTIFICACION = {
    "01": 'Cedula Fisica',
    "02": 'Cedula Juridica',
    "03": '(Dimex) Identificacion Extranjeros',
    [TIPO_DE_IDENTIFICACION_EXTRANJERO_NO_DOMICILIADO]: 'Extranjero No Domiciliado',
    [TIPO_DE_IDENTIFICACION_NO_CONTRIBUYENTE]: 'No Contribuyente',
}

/**
 * Numeros telefonicos especiales de Costa Rica. La bitacora del 22/04/2026 los
 * exceptua del minimo de 8 digitos del campo "NumTelefono". Ampliar la lista si
 * se requiere algun otro numero corto de servicio.
 */
export const SPECIAL_PHONE_NUMBERS = new Set<string>([
    "911",  // Emergencias
    "112",  // Emergencias
    "117",  // Fuerza Publica
    "118",  // Bomberos
    "128",  // Cruz Roja
    "1311", // Transito
    "1322", // Averias telefonicas
]);

export const REPORT_PRINT_FORMAT = "1";
export const REPORT_EXCEL_FORMAT = "2";
export const REPORT_FORMATS = {
    [REPORT_PRINT_FORMAT]: 'PDF',
    [REPORT_EXCEL_FORMAT]: 'Excel'
}

export const MANUAL_TENANT_PAYMENT_TYPE_CODE = "manual";
export const ONVO_TENANT_PAYMENT_TYPE_CODE = "onvo";
export const TENANT_PAYMENT_TYPES = {
    [MANUAL_TENANT_PAYMENT_TYPE_CODE]: "Manual",
    [ONVO_TENANT_PAYMENT_TYPE_CODE]: "ONVO",
}

export type BuyInvoiceType = "Buy" | "Expense";

export const NORMAL_PRINT_FORMANT_ID = "1";
export const TICKET_PRINT_FORMANT_ID = "2";
export const FORMATOS_IMPRESION = {
    [NORMAL_PRINT_FORMANT_ID]: 'Normal',
    [TICKET_PRINT_FORMANT_ID]: 'Como Tiquete'
}

export const DEFAULT_DETAIL_FONT_SIZE = "12";
/**
 * Tamaños de letra disponibles para el detalle de productos en los documentos
 * impresos. Las llaves son el valor en píxeles guardado en `Suscription.detail_font_size`.
 */
export const DETAIL_FONT_SIZES = {
    "12": "Pequeña (12px)",
    "14": "Mediana (14px)",
    "16": "Grande (16px)"
}

export const SITUACION_COMPROBANTE_NORMAL = 1;
export const SITUACION_COMPROBANTE_CONTINGENCIA = 2;
export const SITUACION_COMPROBANTE_SIN_INTERNET = 3;

export type SituacionComprobanteType = typeof SITUACION_COMPROBANTE_NORMAL | typeof SITUACION_COMPROBANTE_CONTINGENCIA | typeof SITUACION_COMPROBANTE_SIN_INTERNET

export const EINVOICE_INVOICE_TYPE_CODE = "0"
export const EINVOICE_TICKET_TYPE_CODE = "1"
export const EINVOICE_EXPORT_INVOICE_TYPE_CODE = "2"

export const EINVOICE_TYPES = {
    [EINVOICE_INVOICE_TYPE_CODE]: 'Factura Electronica',
    [EINVOICE_TICKET_TYPE_CODE]: 'Tiquete Electronico',
    [EINVOICE_EXPORT_INVOICE_TYPE_CODE]: 'Factura Electronica Exportacion',
}

/** Los codigos de actividad economica de Hacienda son siempre de 6 caracteres (ej. "4530.0"). */
export const ACTIVITY_CODE_LENGTH = 6

export const ALL_STATUS_FILTER_TYPE_CODE = "todas"
export const PAYED_STATUS_FILTER_TYPE_CODE = "pagadas"
export const NULL_STATUS_FILTER_TYPE_CODE = "anuladas"
export const PENDING_STATUS_FILTER_TYPE_CODE = "pendientes"
export const EXPIRED_STATUS_FILTER_TYPE_CODE = "vencidas"

export const STATUS_FILTER = {
    [ALL_STATUS_FILTER_TYPE_CODE]: 'Todas',
    [PAYED_STATUS_FILTER_TYPE_CODE]: 'Pagadas',
    [NULL_STATUS_FILTER_TYPE_CODE]: 'Anuladas',
    [PENDING_STATUS_FILTER_TYPE_CODE]: 'Facturas Pendientes',
    [EXPIRED_STATUS_FILTER_TYPE_CODE]: 'Facturas Vencidas'
}

export const INVOICE_CONTADO_TYPE_CODE = "0"
export const INVOICE_CREDIT_TYPE_CODE = "1"
export const INVOICE_HOLDING_TYPE_CODE = "2"

export const INVOICE_TYPES = {
    [INVOICE_CONTADO_TYPE_CODE]: 'Contado',
    [INVOICE_CREDIT_TYPE_CODE]: 'Credito',
    [INVOICE_HOLDING_TYPE_CODE]: 'Apartado'
}

export const SETTLEMENT_TYPES = {
    [INVOICE_CONTADO_TYPE_CODE]: 'Contado',
    [INVOICE_CREDIT_TYPE_CODE]: 'Credito',
}

export const ON_INVOICE_DISCOUNT_TYPE_CODE = "1"
export const CREDIT_NOTE_DISCOUNT_TYPE_CODE = "2"

export const DISCOUNT_TYPES = {
    [ON_INVOICE_DISCOUNT_TYPE_CODE]: 'Descuento en Factura',
    [CREDIT_NOTE_DISCOUNT_TYPE_CODE]: 'Descuento como nota de Credito'
}

export const INVOICE_PAYMENT_ACTIVE_STATUS = "active"
export const INVOICE_PAYMENT_CANCELLED_STATUS = "cancelled"

export const CLIENT_CREDIT_ACTIVE_STATUS = "active"
export const CLIENT_CREDIT_APPLIED_STATUS = "applied"
export const CLIENT_CREDIT_CANCELLED_STATUS = "cancelled"
export const CLIENT_CREDIT_STATUS_LABELS: Record<string, string> = {
    [CLIENT_CREDIT_ACTIVE_STATUS]: "Activo",
    [CLIENT_CREDIT_APPLIED_STATUS]: "Aplicado",
    [CLIENT_CREDIT_CANCELLED_STATUS]: "Anulado",
}
export const CLIENT_CREDIT_DOCUMENT_PREFIX = "SF"
/** Colores del estado en la lista de saldos a favor: activo azul, aplicado verde, anulado rojo. */
export const CLIENT_CREDIT_STATUS_COLORS: Record<string, { bg: string, text: string }> = {
    [CLIENT_CREDIT_ACTIVE_STATUS]: { bg: "#337ab7", text: "#fff" },
    [CLIENT_CREDIT_APPLIED_STATUS]: { bg: "#06B900", text: "#fff" },
    [CLIENT_CREDIT_CANCELLED_STATUS]: { bg: "#dc3545", text: "#fff" },
}

// export const CURRENCIES = {
//     "CRC": 'Colones $',
//     "USD": 'Dolares $'
// }

export const MERCHANDISE_PRODUCT_TYPE_CODE = 1
export const SERVICE_PRODUCT_TYPE_CODE = 2

export const PRODUCT_TYPES = {
    [MERCHANDISE_PRODUCT_TYPE_CODE]: 'Mercancia',
    [SERVICE_PRODUCT_TYPE_CODE]: 'Servicio'
}

export const CLIENT_TYPES = {
    0: 'Contado',
    1: 'Credito'
}

export const PROVIDER_CASH_INVOICE_TYPE = 0
export const PROVIDER_CREDIT_INVOICE_TYPE = 1

export const PROVIDER_TYPES: Record<any, string> = {
    [PROVIDER_CASH_INVOICE_TYPE]: 'Contado',
    [PROVIDER_CREDIT_INVOICE_TYPE]: 'Credito'
}

export const PROVIDER_INVOICE_CASH_TYPES = "01"
export const PROVIDER_INVOICE_CREDITO_TYPES = "02"
export const PROVIDER_INVOICE_TYPES: Record<any, string> = {
    [PROVIDER_INVOICE_CASH_TYPES]: 'Contado',
    [PROVIDER_INVOICE_CREDITO_TYPES]: 'Credito'
}

export const PROVIDER_CREDITNOTE_TYPE_CODE = 0
export const PROVIDER_DEBITNOTE_TYPE_CODE = 1
export const PROVIDER_NOTE_TYPES: Record<any, string> = {
    [PROVIDER_CREDITNOTE_TYPE_CODE]: 'Nota de Credito',
    [PROVIDER_DEBITNOTE_TYPE_CODE]: 'Nota de Debito'
}

export const PRODUCT_VISIBILITIES = {
    0: 'No Visible',
    1: 'Global',
    2: "Solo Facturacion"
}

export const IVA_TAX_TYPE = "01"
export const TAXES_TYPES = {
    [IVA_TAX_TYPE]: "Impuesto al Valor Agregado (IVA)",
    "02": "Impuesto Selectivo de Consumo",
    "03": "Impuesto unico a los combustivos",
    "04": "Impuesto especifico de bebidas alcohólicas",
    "05": "Impuesto especifico sobre las bebidas envasadas sin contenido alcoholico y jabones de tocador",
    "06": "Impuesto a los productos de tabaco",
    "07": "IVA (cálculo especial)",
    "08": "IVA Régimen de Bienes Usados (Factor)",
    "12": "Impuesto Especifico al Cemento",
    "99": "Otros",
}

export const TAXES_PERCENTAGES = {
    "08": "Tarifa General 13%",
    "01": "Tarifa 0% (Exento)",
    "02": "Tarifa Reducida 1%",
    "03": "Tarifa reducida 2%",
    "04": "Tarifa reducida 4%",
    "05": "Transitorio 0%",
    "06": "Transitorio 4%",
    "07": "Transitorio 8%",
    "09": "Tarifa reducida 0.5%",
    "10": "Tarifa Exenta",
    "11": "Tarifa 0% sin derecho a crédito"
}

export const TAXES_CONDITIONS_GENERAL_CREDITO_IVA_CODE = "01"

export const TAXES_CONDITIONS = {
    [TAXES_CONDITIONS_GENERAL_CREDITO_IVA_CODE]: "General Credito IVA",
    "02": "General Crédito parcial del IVA",
    "03": "Bienes de Capital",
    "04": "Gasto corriente no genera crédito",
    "05": "Proporcionalidad",
}

export const SALE_CONDITIONS = {
    0: '01', //"Contado",
    1: '02', //"Credito",
    3: '03', //"Consignacion",
    2: '04', //"Apartado",
    4: '05', //"Arrendamiento con opcion de compra",
    5: '06', //"Arrendamiento en funcion financiera",
    6: '99', //"Otros",
    7: '07', // Cobro a favor de un tercero,
    8: '08', // Servicios prestados al Estado a crédito ,
    9: '09', // pago del servicio prestado al estado
    10: '10', // Venta a crédito en IVA hasta 90 días (Artículo 27, LIVA)
    11: '11', // pago de venta a crédito en IVA hasta 90 dias,
    12: '12', // Venta Mercancía No Nacionalizada,
    13: '13', // Venta Bienes Usados No Contribuyente
    14: '14', // Arrendamiento Operativo
    15: '15'  // Arrendamiento Financiero
}

export const CASH_PAYMENT_WAY_CODE = 1
export const CARD_PAYMENT_WAY_CODE = 2
export const DEBIT_NOTE_PAYMENT_WAY_CODE = 3
export const CHEQUE_PAYMENT_WAY_CODE = 4
export const TRANSFER_PAYMENT_WAY_CODE = 5
export const THIRD_PAYMENT_WAY_CODE = 6
export const SINPE_PAYMENT_WAY_CODE = 7
export const DIGITAL_PLATFORM_PAYMENT_WAY_CODE = 8
export const OTHER_PAYMENT_WAY_CODE = 99

export const PAYMENT_METHODS: Record<number, { atvCode: string; label: string }> = {
    [CASH_PAYMENT_WAY_CODE]: {
        atvCode: '01',
        label: 'Efectivo'
    },
    [CARD_PAYMENT_WAY_CODE]: {
        atvCode: '02',
        label: 'Tarjeta'
    },
    [DEBIT_NOTE_PAYMENT_WAY_CODE]: {
        atvCode: '99',
        label: 'Nota de Debito'
    },
    [CHEQUE_PAYMENT_WAY_CODE]: {
        atvCode: '03',
        label: 'Cheque'
    },
    [TRANSFER_PAYMENT_WAY_CODE]: {
        atvCode: '04',
        label: 'Transferencia Bancaria'
    },
    [THIRD_PAYMENT_WAY_CODE]: {
        atvCode: '05',
        label: 'Recaudado por terceros'
    },
    [SINPE_PAYMENT_WAY_CODE]: {
        atvCode: '06',
        label: 'Sinpe Movil'
    },
    [DIGITAL_PLATFORM_PAYMENT_WAY_CODE]: {
        atvCode: '07',
        label: 'Plataforma Digital'
    },
    [OTHER_PAYMENT_WAY_CODE]: {
        atvCode: '99',
        label: 'Otros'
    }
}

export const REFERENCES_INFORMATION = {
    "Invoice": '01',
    "Debitnote": '02',
    "Creditnote": '03',
    "tiquete": '04',
    "notadespacho": '05',
    "contrato": '06',
    "procedimiento": '07',
    "contigencia": '08',
    "devolucion": '09',
    "comprobanteRechazado": '10',
    "sustituyeRechazada": '11',
    "sustituyeExportacion": '12',
    "facturacionMesVencido": '13',
    "regimenEspecial": '14',
    "sustityeFacCompra": '15',
    "ComprobantePronoDomicilio": '16',
    "notaCreditoFacturaElectronicaCompra": '17',
    "notaDebitpFacturaElectronicaCompra": '18',
    "facturaExportacion": '19',
    "reciboElectronicoPago": '20',
    "otros": '99'
}
export const REFERENCES_CODES = {
    "anular": '01', //Anula Documento de Referencia
    "corregir_monto": '02', //Corrige monto (v4.4)
    "corregir_texto": '02', //Alias de retrocompatibilidad. v4.4 fusiona en código 02 "Corrige monto"
    "referencia": '04', //Referencia a otro documento
    "sustituir_provisional": '05', //Sustituye comprobante provisional por contingencia
    "devolucion": '06', //Devolución de mercancía
    "sustituir": '07', //Sustituye comprobante electrónico
    "facturaEndosada": '08', //Factura Endosada
    "notaDeCredito": '09', //Nota de crédito financiera
    "notaDeDebito": '10', //Nota de débito financiera
    "proveedorNoDomiciliado": '11', //Proveedor No Domiciliado
    "creditoExoneracion": '12', //Crédito por exoneración posterior a la facturación
    "anularErrorMaterial": '13', //Anula documento de referencia por error material (v4.4)
    "corregirMontoErrorMaterial": '14', //Corrige monto por error material (v4.4)
    "sustituirErrorMaterial": '15', //Sustituye comprobante electrónico por error material (v4.4)
    "sustituirRechazado": '16', //Sustituye comprobante electrónico rechazado (v4.4)
    "pagoComprobante": '17', //Pago a comprobante electrónico (REP - v4.4)
    "otros": '99' //Otros
}

export type ReferenceCatalogOption = {
    value: string;
    label: string;
};

export const REFERENCE_TYPE_OPTIONS: ReferenceCatalogOption[] = [
    { value: '01', label: 'Factura electrónica' },
    { value: '02', label: 'Nota de débito electrónica' },
    { value: '03', label: 'Nota de crédito electrónica' },
    { value: '04', label: 'Tiquete electrónico' },
    { value: '05', label: 'Nota de despacho' },
    { value: '06', label: 'Contrato' },
    { value: '07', label: 'Procedimiento' },
    { value: '08', label: 'Comprobante emitido en contingencia' },
    { value: '09', label: 'Devolución mercadería' },
    { value: '10', label: 'Comprobante electrónico rechazado por el Ministerio de Hacienda' },
    { value: '11', label: 'Sustituye factura rechazada por el Receptor del comprobante' },
    { value: '12', label: 'Sustituye factura de exportación' },
    { value: '13', label: 'Facturación mes vencido' },
    { value: '14', label: 'Comprobante aportado por contribuyente de Régimen Especial' },
    { value: '15', label: 'Sustituye factura electrónica de compra' },
    { value: '16', label: 'Comprobante de Proveedor No Domiciliado' },
    { value: '17', label: 'Nota de crédito a factura electrónica de compra' },
    { value: '18', label: 'Nota de débito a factura electrónica de compra' },
    { value: '19', label: 'Factura electrónica de exportación' },
    { value: '20', label: 'Recibo electrónico de pago' },
    { value: '99', label: 'Otros' },
];

/**
 * Codigos de referencia de la nota 9 del anexo v4.4. Efecto contable segun la
 * nota al pie de esa tabla:
 * - Codigos 01, 02 y 06: impactan el periodo en el que se genero la nota. Tras un
 *   01, el comprobante que lo sustituye debe referenciarse con el codigo 07.
 * - Codigos 13 y 14 (error material): impactan el periodo del comprobante original.
 *   Tras un 13, el comprobante que lo sustituye debe referenciarse con el codigo 15.
 * - Codigo 12: exclusivo de notas de credito financieras por una exoneracion
 *   concreta aprobada despues de la transaccion; impacta el periodo de la nota.
 * - Codigo 17: de uso exclusivo del Recibo Electronico de Pago.
 */
export const REFERENCE_CODE_OPTIONS: ReferenceCatalogOption[] = [
    { value: '01', label: 'Anula documento de referencia' },
    { value: '02', label: 'Corrige monto' },
    { value: '04', label: 'Referencia a otro documento' },
    { value: '05', label: 'Sustituye comprobante provisional por contingencia' },
    { value: '06', label: 'Devolución de mercancía' },
    { value: '07', label: 'Sustituye comprobante electrónico' },
    { value: '08', label: 'Factura endosada' },
    { value: '09', label: 'Nota de crédito financiera' },
    { value: '10', label: 'Nota de débito financiera' },
    { value: '11', label: 'Proveedor no domiciliado' },
    { value: '12', label: 'Nota de crédito financiera por exoneración posterior a la facturación' },
    { value: '13', label: 'Anula documento de referencia por error material' },
    { value: '14', label: 'Corrige monto por error material' },
    { value: '15', label: 'Sustituye comprobante electrónico por error material' },
    { value: '16', label: 'Sustituye comprobante electrónico rechazado' },
    { value: '17', label: 'Pago a comprobante electrónico' },
    { value: '99', label: 'Otros' },
];

// Códigos de TipoDocIR (Nota 10) que requieren clave numérica de 50 posiciones según la nota 10
export const REFERENCE_TYPES_REQUIRING_NUMERIC_KEY = new Set<string>([
    '01', '02', '03', '04', '09', '11', '12', '15', '17', '18', '19', '20',
]);

export const getReferenceTypeLabel = (value: string): string =>
    REFERENCE_TYPE_OPTIONS.find((option) => option.value === value)?.label || value;

export const getReferenceCodeLabel = (value: string): string =>
    REFERENCE_CODE_OPTIONS.find((option) => option.value === value)?.label || value;

/** Codigo de "Cobro de un tercero": exige IdentificacionTercero y NombreTercero. */
export const OTHER_CHARGE_TYPE_THIRD_PARTY = '04';
/** Codigo del impuesto de servicio 10%. Reemplaza la vieja linea ficticia "propina". */
export const OTHER_CHARGE_TYPE_SERVICE_TAX = '06';
/** Codigo "Otros Cargos": exige describir el tipo en TipoDocumentoOTROS. */
export const OTHER_CHARGE_TYPE_OTHERS = '99';

/**
 * Catalogo TipoDocOtrosCargosType del XSD v4.4 (nota 16 del anexo, pag. 80).
 * Es un catalogo cerrado: un valor fuera de esta lista lo detecta Hacienda cuando
 * el consecutivo ya se quemo, por eso el control en la UI siempre es un select.
 */
export const OTHER_CHARGE_TYPE_OPTIONS: ReferenceCatalogOption[] = [
    { value: '01', label: 'Contribución parafiscal' },
    { value: '02', label: 'Timbre de la Cruz Roja' },
    { value: '03', label: 'Timbre de Benemérito Cuerpo de Bomberos de Costa Rica' },
    { value: OTHER_CHARGE_TYPE_THIRD_PARTY, label: 'Cobro de un tercero' },
    { value: '05', label: 'Costos de Exportación' },
    { value: OTHER_CHARGE_TYPE_SERVICE_TAX, label: 'Impuesto de servicio 10%' },
    { value: '07', label: 'Timbre de Colegios Profesionales' },
    { value: '08', label: 'Depósitos de Garantía' },
    { value: '09', label: 'Multas o Penalizaciones' },
    { value: '10', label: 'Intereses Moratorios' },
    { value: OTHER_CHARGE_TYPE_OTHERS, label: 'Otros Cargos' },
];

/**
 * Mismo catalogo en el shape `Record<codigo, etiqueta>` que consumen los selects de
 * `@mavichpe/dynamic-forms`. Se deriva de OTHER_CHARGE_TYPE_OPTIONS para que el catalogo
 * siga teniendo una sola definicion. Se antepone el codigo porque el usuario lo cruza
 * contra la nota 16 del anexo.
 */
export const OTHER_CHARGE_TYPES: Record<string, string> = Object.fromEntries(
    OTHER_CHARGE_TYPE_OPTIONS.map((option) => [option.value, `${option.value} - ${option.label}`]),
);

export const getOtherChargeTypeLabel = (value: string): string =>
    OTHER_CHARGE_TYPE_OPTIONS.find((option) => option.value === value)?.label || value;

/**
 * Valores del complexType IdentificacionType para el tercero de un cargo.
 * No se reusa TIPOS_DE_IDENTIFICACION porque a ese mapa le falta el 04 (NITE),
 * que el XSD si admite en este nodo.
 */
export const OTHER_CHARGE_THIRD_PARTY_ID_TYPE_OPTIONS: ReferenceCatalogOption[] = [
    { value: TIPO_DE_IDENTIFICACION_FISICA, label: 'Cédula Física' },
    { value: TIPO_DE_IDENTIFICACION_JURIDICA, label: 'Cédula Jurídica' },
    { value: TIPO_DE_IDENTIFICACION_DIMEX, label: 'DIMEX' },
    { value: TIPO_DE_IDENTIFICACION_NITE, label: 'NITE' },
    { value: TIPO_DE_IDENTIFICACION_EXTRANJERO_NO_DOMICILIADO, label: 'Extranjero No Domiciliado' },
    { value: TIPO_DE_IDENTIFICACION_NO_CONTRIBUYENTE, label: 'No Contribuyente' },
];

/** Version `Record` de OTHER_CHARGE_THIRD_PARTY_ID_TYPE_OPTIONS para los selects. */
export const OTHER_CHARGE_THIRD_PARTY_ID_TYPES: Record<string, string> = Object.fromEntries(
    OTHER_CHARGE_THIRD_PARTY_ID_TYPE_OPTIONS.map((option) => [option.value, option.label]),
);

/** Modo en que se define el monto de un cargo. Radio del editor de otros cargos. */
export const OTHER_CHARGE_AMOUNT_MODES: Record<string, string> = {
    percent: "Porcentaje (%)",
    amount: "Monto fijo",
};

/** Sucursal por defecto (casa matriz) cuando el usuario no tiene una asignada. */
export const DEFAULT_STORE = "001";
/** Terminal por defecto cuando el usuario no tiene `custom:terminalId` asignado. */
export const DEFAULT_TERMINAL = "00001";

export const EDOCUMENT_TYPE = {
    "Invoice": '01',
    "Debitnote": '02',
    "Creditnote": '03',
    "Ticket": '04',
    "aceptada": '05', //Transferencia - deposito bancario
    "aceptada_parcial": '06', //Recaudado por terceros
    "rechazada": '07',
    "BuyInvoice": '08',
    "ExportInvoice": '09',
    "PayRecibe": '10'
}

export type EDocumentTypeKey = keyof typeof EDOCUMENT_TYPE;

// Resuelve TipoDocIR (Nota 10) por defecto para una factura/tiquete/exportación.
// Por convención del producto:
//   - Tiquete electrónico  -> 04
//   - Factura electrónica  -> 01
//   - Factura de exportación -> 01 (código 19 disponible para selección manual)
export const resolveReferencedDocumentType = (einvoiceKind?: string | null): string => {
    switch (einvoiceKind) {
        case EINVOICE_TICKET_TYPE_CODE:
            return REFERENCES_INFORMATION.tiquete;
        case EINVOICE_EXPORT_INVOICE_TYPE_CODE:
        case EINVOICE_INVOICE_TYPE_CODE:
        default:
            return REFERENCES_INFORMATION.Invoice;
    }
};

export type ReferencedDocumentClass = "Invoice" | "Creditnote" | "Debitnote" | "BuyInvoice";

// Resuelve el TipoDocIR por defecto considerando rechazo por Hacienda.
// Rechazado -> 10 (Comprobante electrónico rechazado por el Ministerio de Hacienda).
export const resolveDefaultTipoDocIR = (params: {
    documentClass: ReferencedDocumentClass;
    einvoiceKind?: string | null;
    isRejectedByHacienda?: boolean;
}): string => {
    if (params.isRejectedByHacienda) {
        return REFERENCES_INFORMATION.comprobanteRechazado;
    }
    switch (params.documentClass) {
        case "Creditnote":
            return REFERENCES_INFORMATION.Creditnote;
        case "Debitnote":
            return REFERENCES_INFORMATION.Debitnote;
        case "BuyInvoice":
            return REFERENCES_INFORMATION.ComprobantePronoDomicilio;
        case "Invoice":
        default:
            return resolveReferencedDocumentType(params.einvoiceKind || undefined);
    }
};

export const ATV_ACCEPTED_RESPONSE_CODE = "01";
export const ATV_PARTIAL_ACCEPTED_RESPONSE_CODE = "02";
export const ATV_REJECTED_RESPONSE_CODE = "03";

export const ATV_RESPONSES = {
    [ATV_ACCEPTED_RESPONSE_CODE]: "Aceptada",
    [ATV_PARTIAL_ACCEPTED_RESPONSE_CODE]: "Aceptada Parcial",
    [ATV_REJECTED_RESPONSE_CODE]: "Rechazada"
}
export const ATV_RESPONSES_CODE_TO_EDOCUMET_TYPE: Record<string, EDocumentTypeKey> = {
    [ATV_ACCEPTED_RESPONSE_CODE]: "aceptada",
    [ATV_PARTIAL_ACCEPTED_RESPONSE_CODE]: "aceptada_parcial",
    [ATV_REJECTED_RESPONSE_CODE]: "rechazada"
}
export const TAXES_PERCENTAGES_VALUES: Record<string, string> = {
    "08": "13",
    "01": "0",
    "02": "1",
    "03": "2",
    "04": "4",
    "05": "0",
    "06": "4",
    "07": "8",
    "09": "0.5",
    "10": "0",
    "11": "0"
}

export const SERVICE_CABYS_INIT_DIGITS = [6, 7, 8, 9]

/**
 * Unidades de medida que el sistema trata como servicio al armar los totales del
 * comprobante (ver `addEDocumentCalculateFields` en `src/atv/submitInvoice.ts`).
 *
 * Ojo: Hacienda no usa este criterio, clasifica por el primer digito del CAByS
 * (`SERVICE_CABYS_INIT_DIGITS`). La divergencia entre ambos es la causa numero uno de los
 * rechazos por totales que no cuadran, y es lo que reconcilia `@utils/edocumentDiagnostic`.
 */
export const ATV_SERVICES_SIZING_UNID = ["Al", "Alc", "Cm", "I", "Os", "Sp", "Spe", "St", "d", "h"]

/** Codigo de la nota 8.1 para la tarifa reducida del 4%. */
export const REDUCED_4_TAX_PERCENTAGE_CODE = "04"

/**
 * Prefijos CAByS de los servicios de salud humana (grupo 931), usados para el
 * campo "TotalIVADevuelto" del resumen del comprobante. Ej: 9310100000100
 * (consulta de medico general) o 9319999000000 (servicios de salud humana n.c.p.).
 */
export const HEALTH_SERVICE_CABYS_PREFIXES = ["931"]

export const TAXES_PERCENTAGES_CODE_BY_PERCENTAGE: Record<string, string> = {
    "13": "08",
    "0": "01",
    "1": "02",
    "2": "03",
    "4": "04",
    "8": "07",
    "0.5": "09",
}

/**
 * Tarifas de IVA que la ley fija por quien compra y no por el producto. El CAByS trae la
 * tarifa general del bien (Hacienda la publica solo como referencia), asi que estas ventas
 * llevan otro codigo de tarifa sin nodo de exoneracion. Las claves se guardan en
 * `Client.tax_regime` / `Invoice.tax_regime`: no renombrarlas.
 */
export type ReceptorTaxRegime = {
    label: string;
    /** Porcentaje que se factura, en el mismo formato que `porcentaje_impuesto` de la linea. */
    rate: string;
    /** Codigo de tarifa v4.4 que corresponde a `rate`. */
    codigoTarifa: string;
    legalBasis: string;
}

/** Sin tarifa especial: cada linea con la tarifa de su producto. Equivale a vacio. */
export const RECEPTOR_TAX_REGIME_GENERAL = "general"
export const RECEPTOR_TAX_REGIME_HIGHER_EDUCATION = "edu_superior_estatal"
export const RECEPTOR_TAX_REGIME_AGRICULTURAL_PRODUCER = "agro_mag"

export const RECEPTOR_TAX_REGIMES: Record<string, ReceptorTaxRegime> = {
    [RECEPTOR_TAX_REGIME_HIGHER_EDUCATION]: {
        label: "Educacion superior estatal 2% (UCR, TEC, UNA, UNED, UTN, CONARE, SINAES)",
        rate: "2",
        codigoTarifa: "03",
        legalBasis: "Ley del IVA, articulo 11, inciso 2.d",
    },
    [RECEPTOR_TAX_REGIME_AGRICULTURAL_PRODUCER]: {
        label: "Productor agropecuario inscrito en el MAG 1%",
        rate: "1",
        codigoTarifa: "02",
        legalBasis: "Ley del IVA, articulo 11; resolucion DGT-MAG R52-2019",
    },
}

/** Opciones del select (el select no se puede limpiar, por eso "general" es una opcion). */
export const RECEPTOR_TAX_REGIME_OPTIONS: Record<string, string> = {
    [RECEPTOR_TAX_REGIME_GENERAL]: "General (tarifa del producto)",
    ...Object.fromEntries(Object.entries(RECEPTOR_TAX_REGIMES).map(([key, regime]) => [key, regime.label])),
}

export const DISCOUNT_CODES = {
    // "01": "Descuento por Regalia", // TODO implementar los calculos necesarios
    "02": "Descuento por Regalía IVA Cobrado al Cliente",
    // "03": "Descuento por Bonificacion", // TODO implementar los calculos necesarios
    "04": "Descuento por volumen",
    "05": "Descuento por Temporada (estacional)",
    "06": "Descuento promocional",
    "07": "Descuento Comercial",
    "08": "Descuento por frecuencia",
    "09": "Descuento sostenido",
}

export const MEASURE_UNITS = {
    "Al": "Alquiler de uso habitacional",
    "Alc": "Alquiler de uso comercial",
    "Cm": "Comisiones",
    "I": "Intereses",
    "Os": "Otro tipo de servicio",
    "Sp": "Servicios Profesionales",
    "Spe": "Servicios personales",
    "St": "Servicios técnicos",
    "1": "uno (indice de refracción)",
    "°C": "grado Celsius",
    "1/m": "1 por metro",
    "A": "Ampere",
    "A/m": "ampere por metro",
    "A/m²": "ampere por metro cuadrado",
    "B": "bel",
    "Bq": "Becquerel",
    "C": "coulomb",
    "C/kg": "coulomb por kilogramo",
    "C/m²": "coulomb por metro cuadrado",
    "C/m³": "coulomb por metro cúbico",
    "cd": "Candela",
    "cd/m²": "candela por metro cuadrado",
    "cm": "centímetro",
    "d": "día",
    "eV": "electronvolt",
    "F": "farad",
    "F/m": "farad por metro",
    "g": "Gramo",
    "Gal": "Galón",
    "Gy": "gray",
    "Gy/s": "gray por segundo",
    "H": "henry",
    "h": "hora",
    "H/m": "henry por metro",
    "Hz": "hertz",
    "J": "Joule",
    "J/(kg·K)": "joule por kilogramo kelvin",
    "J/(mol·K)": "joule por mol kelvin",
    "J/K": "joule por kelvin",
    "J/kg": "joule por kilogramo",
    "J/m³": "joule por metro cúbico",
    "J/mol": "joule por mol",
    "K": "Kelvin",
    "kat": "katal",
    "kat/m³": "katal por metro cúbico",
    "kg": "Kilogramo",
    "kg/m³": "kilogramo por metro cúbico",
    "Km": "Kilometro",
    "Kw": "kilovatios",
    "L": "litro",
    "lm": "lumen",
    "ln": "pulgada",
    "lx": "lux",
    "m": "Metro",
    "m/s": "metro por segundo",
    "m/s²": "metro por segundo cuadrado",
    "m²": "metro cuadrado",
    "m³": "metro cúbico",
    "min": "minuto",
    "mL": "mililitro",
    "mm": "Milímetro",
    "mol": "Mol",
    "mol/m³": "mol por metro cúbico",
    "N": "newton",
    "N/m": "newton por metro",
    "N·m": "newton metro",
    "Np": "neper",
    "º": "grado",
    "Oz": "Onzas",
    "Pa": "pascal",
    "Pa·s": "pascal segundo",
    "rad": "radián",
    "rad/s": "radián por segundo",
    "rad/s²": "radián por segundo cuadrado",
    "s": "Segundo",
    "S": "siemens",
    "sr": "estereorradián",
    "Sv": "sievert",
    "T": "tesla",
    "t": "tonelada",
    "u": "unidad de masa atómica unificada",
    "ua": "unidad astronómica",
    "Unid": "Unidad",
    "V": "volt",
    "V/m": "volt por metro",
    "W": "Watt",
    "W/(m·K)": "watt por metro kevin",
    "W/(m²·sr)": "watt por metro cuadrado estereorradián",
    "W/m²": "watt por metro cuadrado",
    "W/sr": "watt por estereorradián",
    "Wb": "weber",
    "Ω": "ohm"
}
export const COLON_CURRENCY_ID = 1
export const DOLLAR_CURRENCY_ID = 2

export const CURRENCIES: Record<number, string> = {
    [COLON_CURRENCY_ID]: 'Colon Costarricense - CRC',
    [DOLLAR_CURRENCY_ID]: 'Dolar Americano  - USD'
}
export const CURRENCIES_SYMBOLS: Record<number, string> = {
    1: '₡',
    2: '$'
}
export const CURRENCIES_CODES: Record<number, string> = {
    1: 'CRC',
    2: 'USD'
}
export const CURRENCIES_CODE_IDS: Record<string, number> = {
    'CRC': 1,
    'USD': 2
}

export const RECIBE_DOCUMENT_INVOICE_TYPE_KEY = "i"
export const RECIBE_DOCUMENT_SETTLEMENT_TYPE_KEY = "s"
export const RECIBE_TYPE_NUMBER_SEPARATOR = "##"
export const RECIBE_DOCUMENT_TYPE_INDEX = 0
export const RECIBE_DOCUMENT_ID_INDEX = 1
/** Prefijo del `motivo` de un recibo de proveedor, que el reparador de recibos parsea. */
export const PROVIDER_RECIBE_MOTIVO_PREFIX = "Pago de Facturas "
export const RECIBE_DOCUMENT_PROVIDER_INVOICE_TYPE_KEY = "pi"
export const RECIBE_DOCUMENT_BUY_INVOICE_TYPE_KEY = "bi"
/**
 * Separa, dentro de `ProviderRecibe.motivo`, la lista de facturas pagadas por completo
 * del detalle del abono parcial. `parseMotivoReferences` corta aqui para no proponer
 * ligar al recibo la factura que quedo pendiente a proposito.
 */
export const PROVIDER_RECIBE_MOTIVO_ABONO_SEPARATOR = " | "
export const PROVIDER_RECIBE_MOTIVO_ABONO_PREFIX = "Abono a "

export const FOUR_PRICES_MANAGEMENT_CODE: string = "1"
export const FORMULA_PRICES_MANAGEMENT_CODE: string = "2"
export const PRICE_MANAGEMENT_OPTIONS: Record<string, string> = {
    [FOUR_PRICES_MANAGEMENT_CODE]: "4 Precios por producto",
    [FORMULA_PRICES_MANAGEMENT_CODE]: "Calcular Mediante Formula"
}

/**
 * Recursos que el API externa de solo lectura puede exponer. Es un catalogo estatico: la
 * fila de `ApiResource` se crea la primera vez que el usuario activa el recurso.
 * El `name` es el identificador estable y forma parte del URL publico; no cambiarlo.
 */
export const API_RESOURCE_CATALOG: Array<{ name: string; label: string; description: string }> = [
    {
        name: "products",
        label: "Productos",
        description: "Catalogo de productos activos con su categoria, existencias, precios sin IVA e imagenes.",
    },
]

export const EXEMPTION_TYPE = {
    "01": "Compras autorizadas por la Dirección General de Tributación",
    "02": "Ventas exentas a diplomáticos",
    "03": "Autorizado por Ley Especial",
    "04": "Exenciones Dirección General de Hacienda Autorización Local Genérica",
    "05": "Exenciones Dirección General de Hacienda Transitorio V (servicios de ingeniería, arquitectura, topografía obra civil)",
    "06": "Servicios turísticos inscritos ante el Instituto Costarricense de Turismo (ICT)",
    "07": "Transitorio XVII (Recolección, Clasificación, almacenamiento de Reciclaje y reutilizable)",
    "08": "Exoneración a Zona Franca",
    "09": "Exoneración de servicios complementarios para la exportación articulo 11 RLIVA",
    "10": "Órgano de las corporaciones municipales",
    "11": "Exenciones Dirección General de Hacienda Autorización de Impuesto Local Concreta",
    "99": "Otros"
}

export const EXONERATION_INSTITUTION = {
    "01": "Ministerio de Hacienda",
    "02": "Ministerio de Relaciones Exteriores y Culto",
    "03": "Ministerio de Agricultura y Ganadería",
    "04": "Ministerio de Economía, Industria y Comercio",
    "05": "Cruz Roja Costarricense",
    "06": "Benemérito Cuerpo de Bomberos de Costa Rica",
    "07": "Asociación Obras del Espíritu Santo",
    "08": "Federación Cruzada Nacional de protección al Anciano (Fecrunapa)",
    "09": "Escuela de Agricultura de la Región Húmeda (EARTH)",
    "10": "Instituto Centroamericano de Administración de Empresas (INCAE)",
    "11": "Junta de Protección Social (JPS)",
    "12": "Autoridad Reguladora de los Servicios Públicos (Aresep)",
    "99": "Otros"
}

export const BANKS = {
    1: 'BCR',
    2: 'BANCREDITO',
    3: 'BAC',
    4: "LAFISE",
    5: "POPULAR",
    6: "CITY",
    7: "DAVIVIENDA",
    8: "SCOTIABANK",
    9: "BANCO GENERAL",
    10: "BNCR",
}

export const MONTHS: Record<string, string> = {
    "01": "Enero",
    "02": "Febrero",
    "03": "Marzo",
    "04": "Abril",
    "05": "Mayo",
    "06": "Junio",
    "07": "Julio",
    "08": "Agosto",
    "09": "Septiembre",
    "10": "Octubre",
    "11": "Noviembre",
    "12": "Diciembre",
};

export const TENANT_STATUSES: Record<string, string> = {
    "active": "Activa",
    "paused": "Pausada",
    "past_due": "Pendiente Pago",
    "canceled": "Cancelada",
    "trialing": "En Periodo de Prueba",
    "pendingPaymentMethod": "Asignacion de Metodo de Pago Pendiente",
    "toCancel": "Suscripcion por Cancelar"
}

const generateLastYears = (count: number = 5): Record<string, string> => {
    const currentYear = new Date().getFullYear();
    const years: Record<string, string> = {};

    for (let i = 0; i < count; i++) {
        const year = (currentYear - i).toString();
        years[year] = year;
    }

    return years;
};

export const LAST_YEARS = generateLastYears(5);